import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import invariant from 'tiny-invariant';

import {
  useDragAndDropAnnouncements,
  useDragAndDropConfig,
  useDragAndDropValidation,
} from './DragAndDropContext';
import { createDragData } from './drag-and-drop-utils';
import type {
  DragHandleProps,
  DragSourceHookResult,
  ItemId,
  ItemIndex,
} from './drag-and-drop.types';

export type DraggableState =
  | { type: 'idle' }
  | { type: 'preview'; container: HTMLElement }
  | { type: 'dragging' };

const IDLE_STATE: DraggableState = { type: 'idle' };
const DRAGGING_STATE: DraggableState = { type: 'dragging' };

interface DragSourceOptions
  extends Pick<DragHandleProps, 'customPreviewRenderer' | 'previewElement'> {}

/**
 * hook for making an element draggable
 */
export function useDragSource(
  itemId: ItemId,
  index: ItemIndex,
  options: DragSourceOptions = {}
): DragSourceHookResult & {
  draggableState: DraggableState;
  setDraggableState: (state: DraggableState) => void;
} {
  const { customPreviewRenderer, previewElement } = options;

  const { instanceId, disabled } = useDragAndDropConfig();
  const { canDrag } = useDragAndDropValidation();
  const { announceStart, announceCancel } = useDragAndDropAnnouncements();

  const dragRef = useRef<HTMLElement>(null);
  const [draggableState, setDraggableState] = useState<DraggableState>(IDLE_STATE);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Check if this item can be dragged
  const canDragItem = useMemo(
    () => !disabled && (!canDrag || canDrag(itemId, index)),
    [disabled, canDrag, itemId, index]
  );

  const isDragging = useMemo(() => draggableState.type === 'dragging', [draggableState.type]);

  // Cleanup function for preview
  const cleanupPreview = useCallback(() => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  useEffect(() => {
    const element = dragRef.current;

    invariant(
      element,
      'useDragSource: Element not found. Ensure dragRef is properly attached to a DOM element.'
    );

    if (!canDragItem) return; // if the element is not draggable, return

    const data = createDragData(itemId, index, instanceId);

    const cleanup = draggable({
      element,
      getInitialData: () => data as unknown as Record<string, unknown>,
      onGenerateDragPreview({ nativeSetDragImage }) {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({
            x: '8px',
            y: '12px',
          }),
          render({ container }) {
            setDraggableState({ type: 'preview', container });

            // Determine which element to use for preview
            let elementToClone = element;
            if (previewElement) {
              if (typeof previewElement === 'function') {
                elementToClone = previewElement() || element;
              } else {
                elementToClone = previewElement;
              }
            }

            // Ensure we have a valid element to clone
            if (!elementToClone) {
              // Provide a safe fallback element
              elementToClone = document.createElement('div');
            }

            // Render the preview content
            if (customPreviewRenderer) {
              cleanupRef.current = customPreviewRenderer(container, elementToClone);
            }
            // Return cleanup that will be called when preview is removed
            return () => {
              cleanupPreview();
              setDraggableState(DRAGGING_STATE);
            };
          },
        });
      },
      onDragStart() {
        setDraggableState(DRAGGING_STATE);
        announceStart(itemId, index);
      },
      onDrop() {
        setDraggableState(IDLE_STATE);
        cleanupPreview();
      },
    });

    return () => {
      cleanup();
      cleanupPreview();
    };
  }, [
    itemId,
    index,
    instanceId,
    canDragItem,
    announceStart,
    cleanupPreview,
    customPreviewRenderer,
    previewElement,
  ]);

  const dragProps = useMemo(
    () => ({
      onMouseDown: (e: React.MouseEvent<HTMLElement>) => {
        if (!canDragItem) e.preventDefault();
      },
      onTouchStart: (e: React.TouchEvent<HTMLElement>) => {
        if (!canDragItem) e.preventDefault();
      },
      onDragStart: (e: React.DragEvent<HTMLElement>) => {
        if (!canDragItem) {
          e.preventDefault();
          return;
        }
      },
      onDragEnd: (e: React.DragEvent<HTMLElement>) => {
        if (e.dataTransfer?.dropEffect === 'none' && draggableState.type !== 'idle') {
          announceCancel(itemId, index);
        }
        setDraggableState(IDLE_STATE);
        cleanupPreview();
      },
      draggable: canDragItem,
      'aria-describedby': `drag-instructions-${itemId}`,
      'data-drag-handle': true as const,
    }),
    [canDragItem, draggableState.type, itemId, index, announceCancel, cleanupPreview]
  );

  return {
    dragProps,
    isDragging,
    canDrag: canDragItem,
    dragRef,
    draggableState,
    setDraggableState,
  };
}
